import { type AskUserInput, questionAnswerOf } from "@esi/shared";
import { Check, MessageCircleQuestionMark, PenLine } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * The agent's clarifying question (openspec change `agent-turn`, D8), on the #384 anatomy of the
 * thread replies (`AgentReplies.tsx`): a dashed card with the question, its context line and the
 * choices as full-width outline buttons in a fieldset. A pick sends the choice as the next user
 * message; "Answer in your own words" focuses the composer with nothing prefilled. Answered, the
 * chosen choice is marked and every choice is disabled, also when the conversation is reopened.
 */
export function QuestionCard(props: {
  question: AskUserInput;
  /** The index of the choice the conversation's next message picked; absent while open. */
  answered?: number | undefined;
  /** The viewer is the one the question asks (the conversation's creator). */
  mine: boolean;
  /** Sends the choice; absent when the viewer may not answer (read-only, shared). */
  onPick?: ((choice: string, index: number) => void) | undefined;
  /** Focuses the composer (an answer in the user's own words). */
  onOwnWords?: (() => void) | undefined;
  /** A reply is streaming: picks wait for it. */
  busy?: boolean;
  /**
   * False once a later message follows the question: unanswered, it is no longer asked (no
   * chip, no pick). Default true.
   */
  open?: boolean;
}) {
  const { t } = useTranslation();
  const { question, answered } = props;
  const chosen = answered === undefined ? undefined : question.choices[answered];
  const open = chosen === undefined;
  const asking = open && props.open !== false;
  const canPick = asking && props.onPick !== undefined;
  return (
    <section
      data-question-card={open ? "open" : "answered"}
      aria-label={t("conversation.question.title")}
      className="flex flex-col gap-2 rounded-xl border border-dashed border-line-strong bg-surface px-3 py-2.5 focus-within:border-solid"
    >
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px]">
        <MessageCircleQuestionMark aria-hidden="true" className="size-4 shrink-0 text-fg-muted" />
        <span className="font-medium text-fg-secondary">{t("conversation.question.title")}</span>
        {open ? (
          props.mine && asking ? (
            <span className="inline-flex items-center rounded-[5px] bg-info-bg px-1.5 py-px text-[11px] font-semibold text-info-fg">
              {t("conversation.question.forYou")}
            </span>
          ) : null
        ) : (
          <span className="text-xs text-fg-muted">
            <span aria-hidden="true">· </span>
            {props.mine
              ? t("conversation.question.youPicked", { choice: chosen })
              : t("conversation.question.picked", { choice: chosen })}
          </span>
        )}
      </p>
      <p className="text-sm break-words whitespace-pre-wrap text-fg">{question.question}</p>
      {question.context ? (
        <p className="-mt-1 text-[13px] break-words text-fg-muted">{question.context}</p>
      ) : null}
      <fieldset className="m-0 flex min-w-0 flex-col gap-1.5 border-0 p-0">
        <legend className="sr-only">{t("conversation.question.choices")}</legend>
        {question.choices.map((choice, i) => {
          const picked = i === answered;
          return (
            <Button
              key={choice}
              type="button"
              variant="outline"
              size="sm"
              aria-pressed={open ? undefined : picked}
              disabled={!canPick || props.busy === true}
              className={cn(
                "h-auto min-h-8 w-full justify-start py-1.5 text-left whitespace-normal",
                picked && "border-line-strong bg-surface-selected disabled:opacity-100",
              )}
              onClick={() => props.onPick?.(choice, i)}
            >
              {picked ? <Check aria-hidden="true" /> : null}
              {choice}
            </Button>
          );
        })}
      </fieldset>
      {canPick && question.allowOther && props.onOwnWords ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="-ml-2 text-fg-secondary"
            onClick={props.onOwnWords}
          >
            <PenLine aria-hidden="true" />
            {t("conversation.question.ownWords")}
          </Button>
        </div>
      ) : null}
    </section>
  );
}

/** The choice each question's answer picked, by the question's tool call (D7). */
export function questionAnswersOf(
  messages: readonly { role: string; parts: readonly unknown[] }[],
): Map<string, number> {
  const answers = new Map<string, number>();
  for (const m of messages) {
    const answer = m.role === "user" ? questionAnswerOf(m.parts) : null;
    if (answer && answer.choiceIndex !== null && !answers.has(answer.toolCallId)) {
      answers.set(answer.toolCallId, answer.choiceIndex);
    }
  }
  return answers;
}
